import { useCallback, useEffect, useState } from 'react';

import { RECENT_VIEW_DAYS, type ViewersResponse } from '@lifetimeline/shared';

import { Avatar } from '../components/Avatar.js';
import { Button } from '../components/Button.js';
import { EmptyState } from '../components/EmptyState.js';
import { Notice } from '../components/Notice.js';
import { UpgradeDialog } from '../components/UpgradeDialog.js';
import { Spinner } from '../components/Spinner.js';
import { useI18n } from '../i18n/index.js';
import { api, errorMessageKey } from '../lib/apiClient.js';
import { relativeTime } from '../lib/relativeTime.js';
import styles from './pageStyles.module.css';

/**
 * 분석 — 누가 내 프로필을 봤나. **목록은 유료다.**
 *
 * 숫자는 누구에게나 보여준다. 몇 명이 봤는지가 결제할 이유이고, 그걸 가리면
 * 무엇을 사는지 알 수가 없다. 잠긴 것은 **누구인지**뿐이다.
 *
 * ⚠️ 잠긴 줄은 **빈 줄**이다. 서버가 이름을 아예 안 보내기 때문에(`viewers`가
 * `null`), 여기서는 숫자만큼 자리를 그린다. 이름을 받아다 흐리게만 그리면
 * 브라우저 도구로 그대로 읽혀서 가린 척만 하는 셈이 된다.
 *
 * 기록 자체는 요금제와 무관하게 쌓인다(서버). 결제한 다음부터 쌓기 시작하면
 * 돈을 낸 직후 보는 첫 화면이 "아무도 안 봤어요"가 된다.
 */
export function AnalyticsPanel() {
  const { t, language } = useI18n();

  const [data, setData] = useState<ViewersResponse | null>(null);
  const [upgrade, setUpgrade] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.get<ViewersResponse>('/api/me/viewers'));
      setError(null);
    } catch (cause) {
      setError(t(errorMessageKey(cause)));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error !== null) {
    return (
      <div className={styles.noticeSlot}>
        <Notice tone="error">{error}</Notice>
      </div>
    );
  }
  if (data === null) {
    return (
      <div className={styles.centered}>
        <Spinner label={t('common.loading')} />
      </div>
    );
  }

  /*
   * 날짜가 아니라 **얼마나 지났는지**를 보여준다. `2026. 9. 27.`은 오늘
   * 날짜를 떠올려 빼야 읽히지만 "3일 전"은 그 계산이 끝난 값이다.
   * 한 달이 넘으면 상대 시간이 뜻을 잃어 날짜로 돌아간다.
   */
  const when = (iso: string) => relativeTime(iso, language, t('viewers.justNow'));

  return (
    <>
      {/* 큰 숫자 하나가 맨 위. 이 화면이 답하는 질문이 그것이다 */}
      <div className={styles.metric}>
        <span className={styles.metricValue}>{data.recentCount}</span>
        <span className={styles.metricLabel}>
          {t('viewers.recentLabel', { days: RECENT_VIEW_DAYS })}
        </span>
        {data.count > data.recentCount ? (
          <span className={styles.metricSub}>{t('viewers.totalLabel', { count: data.count })}</span>
        ) : null}
      </div>

      {data.count === 0 ? (
        <EmptyState label={t('viewers.none')} />
      ) : data.viewers === null ? (
        <>
          {/*
            ⚠️ **지금은 여기로 안 온다.** 유료 칸막이를 걷어서(2026-10) 서버가
            누구에게나 이름을 보낸다. 다시 잠그는 날을 위해 남겨 뒀다 —
            되살리는 법은 `profileViewService`의 `locked`에 적혀 있다.

            잠긴 줄. 자리와 수는 진짜이고 **누구인지만 없다** — 서버가 이름을
            보내지 않는다. 빈 줄이라도 몇 명인지가 눈에 보여야 결제할 이유가
            생긴다.

            줄 오른쪽 끝의 **보기**가 누를 곳이다. 열려 있었다면 그 자리에
            사람이 있었을 테니, 무엇을 여는 버튼인지가 자리로 읽힌다.
            인연 목록의 수락·대기 버튼과 **같은 Button**을 쓴다 — 같은 모양의
            줄에서 오른쪽 버튼만 따로 생기면 다른 종류의 조작으로 보인다.
          */}
          <ul className={styles.personList}>
            {Array.from({ length: Math.min(data.count, 5) }, (_, index) => (
              <li key={index} className={styles.personRow}>
                <span className={styles.lockedRow} aria-hidden="true">
                  <span className={styles.lockedAvatar} />
                  <span className={styles.lockedText}>
                    <span className={styles.lockedLine} />
                    <span className={styles.lockedLineShort} />
                  </span>
                </span>
                <span className={styles.personAction}>
                  <Button size="sm" variant="outline" onClick={() => setUpgrade(true)}>
                    {t('viewers.peek')}
                  </Button>
                </span>
              </li>
            ))}
          </ul>

          <UpgradeDialog open={upgrade} onClose={() => setUpgrade(false)} />
        </>
      ) : (
        <ul className={styles.personList}>
          {data.viewers.map((viewer) => (
            <li key={viewer.handle} className={styles.personRow}>
              <a href={`/@${viewer.handle}`} className={styles.personLink}>
                <Avatar name={viewer.userName} imageUrl={viewer.imageUrl} size={40} />
                <span className={styles.personText}>
                  <strong className={styles.personName}>{viewer.userName}</strong>
                  <span className={styles.personHandle}>{when(viewer.lastViewedAt)}</span>
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
